import { useState, useEffect, useCallback } from 'react';
import { SystemLog, SystemLogListResponse } from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10;

interface UseSystemLogsResult {
  systemLogs: SystemLog[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchSystemLogs: (page: number) => Promise<void>;
  handlePageChange: (page: number) => void;
}

export const useSystemLogs = (): UseSystemLogsResult => {
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSystemLogs = useCallback(async (page: number) => {
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<SystemLogListResponse>(
        `/system_log?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true }
      );
      setSystemLogs(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch system logs: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching system logs.');
      }
      setSystemLogs([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSystemLogs(currentPage);
  }, [currentPage, fetchSystemLogs]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return {
    systemLogs,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchSystemLogs,
    handlePageChange,
  };
};